import React, {useCallback, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Button,
    Link,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalOverlay
} from '@chakra-ui/react'
import {FormattedMessage} from 'react-intl'
import CMSContent from '../components/cms-content'
import useOcapiSite from './useOcapiSite'
import useMultiSite from './use-multi-site'
import {trackAddProductSetToCart, trackAddToCart} from '../tracking/analytics'
import {isProductSet} from '../utils/product-utils'

export const RedirectLinkPartnerModal = ({
    isOpen,
    onClose,
    overlayAssetId,
    onClick,
    partnerBrandButtonColor,
    partnerBrand,
    onOpen,
    partnerLink
}) => (
    <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent backgroundColor="white">
            <ModalCloseButton />
            <ModalBody paddingX={8} paddingTop={16}>
                <Box maxW="100%" overflow="hidden" sx={{'& *': {maxWidth: '100%'}}}>
                    <CMSContent id={overlayAssetId} showError={false} />
                </Box>
            </ModalBody>
            <ModalFooter paddingBottom={8}>
                <Button
                    as={Link}
                    isExternal
                    href={partnerLink}
                    onClick={() => {
                        onOpen?.()
                        onClick?.()
                    }}
                    width="100%"
                    className="add-to-cart"
                    {...(partnerBrandButtonColor
                        ? {
                              bg: partnerBrandButtonColor,
                              borderColor: partnerBrandButtonColor,
                              _hover: {
                                  bg: partnerBrandButtonColor,
                                  color: 'white'
                              }
                          }
                        : {})}
                >
                    <FormattedMessage
                        id="partner_redirect.overlay.label"
                        defaultMessage="Continue to {partnerBrand}"
                        values={{partnerBrand}}
                    />
                </Button>
            </ModalFooter>
        </ModalContent>
    </Modal>
)

RedirectLinkPartnerModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    overlayAssetId: PropTypes.string,
    href: PropTypes.string,
    onClick: PropTypes.func,
    partnerBrandButtonColor: PropTypes.string,
    partnerBrand: PropTypes.string,
    onOpen: PropTypes.func,
    partnerLink: PropTypes.string
}

export const useRedirectLinkPartnerModal = ({product, onClick} = {}) => {
    const [isOpen, setIsOpen] = useState(false)
    const onOpen = useCallback(() => setIsOpen(true), [])
    const onClose = useCallback(() => setIsOpen(false), [])

    const {
        locale: {id: locale, preferredCurrency}
    } = useMultiSite()
    const onClickWithTracking = useCallback(() => {
        if (isProductSet(product)) {
            trackAddProductSetToCart(
                product,
                // basket is not available, because content site
                null,
                null,
                locale,
                preferredCurrency
            )
        } else {
            trackAddToCart(
                product,
                // basket is not available because this is used within content site
                null,
                null,
                preferredCurrency
            )
        }
        onClick?.()
    }, [product, locale, onClick, preferredCurrency])
    const {redirectLinkPartnerAllProducts} = useOcapiSite()
    const rawRedirectValue = useMemo(
        () =>
            redirectLinkPartnerAllProducts &&
            JSON.parse(redirectLinkPartnerAllProducts)?.[locale.replace('-', '_')],
        [redirectLinkPartnerAllProducts, locale]
    )

    const redirectToPartner =
        typeof rawRedirectValue === 'object' ? rawRedirectValue?.link : rawRedirectValue
    const partnerBrandButtonColor =
        typeof rawRedirectValue === 'object' ? rawRedirectValue?.color : null
    const partnerBrand =
        typeof rawRedirectValue === 'object' ? rawRedirectValue?.partnerBrand : null
    const overlayAssetId =
        typeof rawRedirectValue === 'object' ? rawRedirectValue?.overlayAssetId : null

    const redirectLinkPartner = product?.c_redirectLinkPartner
    const partnerLink = redirectLinkPartner || redirectToPartner

    const hasOverlay = !!(redirectToPartner && partnerBrand && overlayAssetId)

    return {
        onOpen: hasOverlay ? onOpen : null,
        isAvailable: !!partnerLink,
        partnerLink,
        partnerBrandButtonColor,
        isOpen,
        onClose,
        overlayAssetId,
        partnerBrand,
        href: partnerLink,
        onClick: onClickWithTracking
    }
}
